import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import NSNavbar from '../../components/NSNavbar';
import NSFooter from '../../components/NSFooter';
import PageWrap from '../../components/PageWrap';
import useDocumentMeta from '../../hooks/useDocumentMeta';
import routeMetaFor from '../../utils/routeMeta';
import { brandPath, parentRouteHref } from '../../utils/brandRouting';
import { SUPPORT_EMAIL } from '../../data/siteContent';
import { APP_LEGAL, EFFECTIVE_DATE, NUTRISCAN_RECOVERY_WINDOW, PUBLISHER } from '../../data/legalContent';

function useNSTheme() {
  useEffect(() => {
    document.body.setAttribute('data-brand', 'nutriscan');
    return () => document.body.removeAttribute('data-brand');
  }, []);
}

const TOC = [
  { id: 'who',      title: '1. Who we are' },
  { id: 'audience', title: '2. Who NutriScan is for' },
  { id: 'info',     title: '3. Information NutriScan handles' },
  { id: 'services', title: '4. Services we use' },
  { id: 'subs',     title: '5. Subscriptions' },
  { id: 'deletion', title: '6. Deleting your account' },
  { id: 'changes',  title: '7. Changes to this notice' },
  { id: 'contact',  title: '8. Contact' },
];

// Categories and service boundaries come from the approved T05 content brief.
// Each row says what may be handled and why; none of them asserts that a
// category is always collected, encrypted, anonymised, or discarded.
const CATEGORIES = [
  {
    category: 'Account and profile information',
    handled: 'Google sign-in identity, account and profile data, self-declared eligibility information, preferences, scan history, and scan-allowance or usage records where the app uses them.',
    purpose: 'Sign-in, account operation, personalised app operation, service integrity, and support. Firebase Authentication, Firestore, Cloud Functions, App Check / Play Integrity, and Remote Config are in scope where used.',
  },
  {
    category: 'Food-label and scan information',
    handled: 'Label text, barcode data, and images or image-derived information you supply for a scan.',
    purpose: 'Analyse a food label and return a result in the app. Gemini AI analysis and Open Food Facts barcode lookup are in scope where used.',
  },
  {
    category: 'Diagnostics and product measurement',
    handled: 'Technical diagnostics, crash reports, and analytics or usage events where the app enables them.',
    purpose: 'Reliability and product improvement. Firebase Analytics and Crashlytics are in scope.',
  },
  {
    category: 'Subscription and entitlement information',
    handled: 'Purchase or subscription status and entitlement identifiers or records.',
    purpose: 'Provide and support an optional subscription. RevenueCat and Google Play Billing are in scope.',
  },
  {
    category: 'Support and export delivery',
    handled: 'The contact details you include in a request and, where the implemented flow uses it, an email address used to deliver an account export.',
    purpose: 'Respond to your request or deliver an export link. SendGrid is in scope for this purpose only.',
  },
];

export default function NSPrivacy() {
  useNSTheme();
  const studioPrivacyHref = parentRouteHref('/privacy');
  const supportHref = parentRouteHref('/contact');
  useDocumentMeta(routeMetaFor('/apps/nutriscan/privacy'));

  return (
    <PageWrap testId="ns-privacy-page">
      <NSNavbar />

      <header className="ns-legal-header" style={{ paddingTop: 'calc(var(--nav-h) + 40px)' }}>
        <div className="wrap" style={{ position: 'relative', zIndex: 1 }}>
          <div className="eyebrow" style={{ justifyContent: 'center', color: 'var(--ns-lime)' }}>NutriScan · Legal</div>
          <h1 className="reveal d1">Privacy Policy</h1>
          <p className="reveal d2" style={{ marginTop: 8 }} data-testid="ns-privacy-effective">
            Effective date: {EFFECTIVE_DATE} · {APP_LEGAL.nutriscan.version}
          </p>
        </div>
      </header>

      <div className="wrap">
        <div className="ns-summary-box reveal">
          <div className="title">The short version</div>
          <div className="ns-summary-grid">
            <div className="ns-summary-item"><i className="fas fa-check"></i><span>NutriScan is intended for people aged 18 and over.</span></div>
            <div className="ns-summary-item"><i className="fas fa-check"></i><span>You sign in with Google, and the app keeps the records it needs to operate your account.</span></div>
            <div className="ns-summary-item"><i className="fas fa-check"></i><span>Label text, barcodes, and images you submit are used to analyse a label and return a result.</span></div>
            <div className="ns-summary-item"><i className="fas fa-check"></i><span>You can ask us to delete your account — see the Account Deletion page.</span></div>
          </div>
        </div>
      </div>

      <section className="section" style={{ paddingTop: 64, background: 'var(--ns-bg)' }}>
        <div className="wrap">
          <div className="legal-layout">
            <nav className="legal-toc">
              <h6>Contents</h6>
              <ul>{TOC.map(s => <li key={s.id}><a href={`#${s.id}`}>{s.title}</a></li>)}</ul>
            </nav>

            <div className="legal-body" style={{ background: 'var(--ns-surf)', borderColor: 'var(--ns-border)' }}>
              <span className="legal-meta-tag" data-testid="ns-privacy-publisher">Published by {PUBLISHER} · Support: {SUPPORT_EMAIL}</span>

              <section id="who"><h2>1. Who we are</h2>
                <p>NutriScan is published by <strong>{PUBLISHER}</strong>. This notice describes how the NutriScan mobile app handles information in {APP_LEGAL.nutriscan.version}.</p>
                <p>Questions about this notice: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.</p>
              </section>

              <section id="audience"><h2>2. Who NutriScan is for</h2>
                <p>NutriScan is intended for people aged <strong>18 and over</strong> and is not directed to children.</p>
                <p>NutriScan provides educational information about food labels. It is not medical, dietary, allergy, diagnostic, or emergency advice — see the <Link to={brandPath('nutriscan', '/terms')}>Terms of Service</Link>.</p>
              </section>

              <section id="info"><h2>3. Information NutriScan handles</h2>
                <p>The table below describes the categories of information the app may handle and why. A category is listed where the app uses it; this notice does not claim that every category is collected in every session.</p>
                <table className="data-table">
                  <thead><tr><th>Category</th><th>What it covers</th><th>Why, and which services are in scope</th></tr></thead>
                  <tbody>
                    {CATEGORIES.map(row => (
                      <tr key={row.category}>
                        <td><strong>{row.category}</strong></td>
                        <td>{row.handled}</td>
                        <td>{row.purpose}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>

              <section id="services"><h2>4. Services we use</h2>
                <p>NutriScan uses the following services, in the roles described above:</p>
                <ul>
                  <li><strong>Firebase</strong> — Authentication, Firestore, Cloud Functions, App Check / Play Integrity, Remote Config, Analytics, and Crashlytics, where used by the app</li>
                  <li><strong>Gemini AI analysis</strong> — analysing the label information you submit for a scan</li>
                  <li><strong>Open Food Facts</strong> — barcode lookup</li>
                  <li><strong>RevenueCat</strong> and <strong>Google Play Billing</strong> — optional subscription and entitlement handling</li>
                  <li><strong>SendGrid</strong> — delivering a reply or an account export link in response to a request</li>
                </ul>
                <p>No other provider is named here. If a service is added or removed, this page and its effective date will be updated.</p>
              </section>

              <section id="subs"><h2>5. Subscriptions</h2>
                <p>A subscription is optional. Purchases are processed by <strong>Google Play Billing</strong>, and entitlement records may be handled through <strong>RevenueCat</strong>.</p>
                <p>You manage or cancel a subscription in Google Play. Deleting your NutriScan account does not by itself cancel a Google Play subscription — see <Link to={brandPath('nutriscan', '/account-deletion')}>Account Deletion</Link>.</p>
              </section>

              <section id="deletion"><h2>6. Deleting your account</h2>
                <p>You can request deletion from inside the app while signed in, or by emailing <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> from the Google account address you use for NutriScan. We verify the request before acting on it.</p>
                <p>A verified request starts a <strong>{NUTRISCAN_RECOVERY_WINDOW}</strong> recovery period, during which you can cancel it. The full process, including what may be retained afterwards, is described on the <Link to={brandPath('nutriscan', '/account-deletion')}>Account Deletion</Link> page.</p>
              </section>

              <section id="changes"><h2>7. Changes to this notice</h2>
                <p>If this notice changes, we update the page and the effective date shown at the top.</p>
              </section>

              <section id="contact"><h2>8. Contact</h2>
                <div className="callout info" style={{ background: 'rgba(22,163,74,0.06)', border: '1px solid rgba(22,163,74,0.2)' }}>
                  <i className="fas fa-envelope" style={{ color: 'var(--ns-green)' }}></i>
                  <span><strong>Email:</strong> <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a><br/>{PUBLISHER}</span>
                </div>
                <p style={{ marginTop: 16, fontSize: '0.85rem' }}>
                  See also the <Link to={brandPath('nutriscan', '/terms')}>NutriScan Terms of Service</Link>, the{' '}
                  <Link to={brandPath('nutriscan', '/account-deletion')}>Account Deletion</Link> page, and the{' '}
                  <Link to={brandPath('nutriscan', '/faq')}>NutriScan FAQ</Link>. For anything else, use{' '}
                  {supportHref
                    ? <a href={supportHref}>TickleApps Support</a>
                    : <Link to="/contact">TickleApps Support</Link>}{' '}
                  or the {studioPrivacyHref
                    ? <a href={studioPrivacyHref}>TickleApps legal directory</a>
                    : <Link to="/privacy">TickleApps legal directory</Link>}.
                </p>
              </section>
            </div>
          </div>
        </div>
      </section>

      <NSFooter />
    </PageWrap>
  );
}
